/**
 * Sensitivity Analysis Panel (SPRINT-25 F005)
 *
 * Main panel within the Deal Financial Hub "Sensitivity" sub-tab.
 * Provides:
 * - Empty state with "Create your first scenario" prompt + Add Scenario button
 * - Comparison grid (SensitivityComparisonGrid) when scenarios exist
 * - "Add Scenario" button (always visible)
 * - "Recalculate All" button (visible when scenarios exist)
 * - Add/Edit scenario modal (SensitivityScenarioModal)
 * - Delete confirmation dialog
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, RefreshCw, Loader2, TrendingDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { SensitivityComparisonGrid } from "./sensitivity-comparison-grid";
import { SensitivityScenarioModal } from "./sensitivity-scenario-modal";
import type { DealSensitivityScenario } from "@shared/schema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SensitivityAnalysisPanelProps {
  dealId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SensitivityAnalysisPanel({ dealId }: SensitivityAnalysisPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingScenario, setEditingScenario] = useState<DealSensitivityScenario | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DealSensitivityScenario | null>(null);

  // -----------------------------------------------------------------------
  // Fetch scenarios
  // -----------------------------------------------------------------------

  const { data: scenarios = [], isLoading } = useQuery<DealSensitivityScenario[]>({
    queryKey: [`/api/financial/deals/${dealId}/sensitivity-scenarios`],
    enabled: !!dealId,
  });

  // -----------------------------------------------------------------------
  // Recalculate mutation
  // -----------------------------------------------------------------------

  const recalculateMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", `/api/financial/deals/${dealId}/calculate-sensitivity`, {
        periodLabel: "FY2024",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/sensitivity-scenarios`],
      });
      toast({
        title: "Sensitivity analysis calculated",
        description: `Results updated for ${scenarios.length} scenario${scenarios.length !== 1 ? "s" : ""}.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Calculation failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // -----------------------------------------------------------------------
  // Delete mutation
  // -----------------------------------------------------------------------

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/financial/deals/${dealId}/sensitivity-scenarios/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/sensitivity-scenarios`],
      });
      toast({ title: "Scenario deleted", description: "The scenario has been removed." });
      setDeleteTarget(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete scenario",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  function handleAddScenario() {
    setEditingScenario(null);
    setModalOpen(true);
  }

  function handleEditScenario(scenario: DealSensitivityScenario) {
    setEditingScenario(scenario);
    setModalOpen(true);
  }

  function handleDeleteScenario(scenario: DealSensitivityScenario) {
    setDeleteTarget(scenario);
  }

  function confirmDelete() {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
    }
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const hasScenarios = scenarios.length > 0;

  return (
    <Card
      className="border-0 shadow-none"
      data-testid="panel-sensitivity-analysis"
    >
      <CardHeader className="px-0 py-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-medium">Sensitivity Analysis</CardTitle>

        <div className="flex items-center gap-2">
          {hasScenarios && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => recalculateMutation.mutate()}
              disabled={recalculateMutation.isPending}
              data-testid="button-recalculate-all"
            >
              {recalculateMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1.5" />
              )}
              Recalculate All
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleAddScenario}
            data-testid="button-add-scenario"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Scenario
          </Button>
        </div>
      </CardHeader>

      <CardContent className="px-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !hasScenarios ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <TrendingDown className="h-10 w-10 text-muted-foreground mb-4" />
            <h3 className="text-sm font-medium mb-1">No sensitivity scenarios yet</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
              Create your first scenario to analyse how rate changes or income variations affect deal
              serviceability.
            </p>
            <Button size="sm" onClick={handleAddScenario} variant="outline">
              <Plus className="h-4 w-4 mr-1.5" />
              Create your first scenario
            </Button>
          </div>
        ) : (
          <SensitivityComparisonGrid
            scenarios={scenarios}
            onEdit={handleEditScenario}
            onDelete={handleDeleteScenario}
          />
        )}
      </CardContent>

      {/* Add/Edit Modal */}
      <SensitivityScenarioModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        dealId={dealId}
        scenario={editingScenario}
        onSaved={() => {
          queryClient.invalidateQueries({
            queryKey: [`/api/financial/deals/${dealId}/sensitivity-scenarios`],
          });
        }}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Scenario</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteTarget?.scenarioName}&quot;? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-scenario"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
